import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail, MapPin, Phone, Clock, Send, CheckCircle, ArrowLeft, Building2, User, MessageSquare, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import PhoneInput from '../components/ui/PhoneInput';
import EmailInput from '../components/ui/EmailInput';
import BackButton from '../components/BackButton';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    entreprise: '',
    sujet: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  const subjects = [
    { value: 'information', label: t('contact.subject_info') },
    { value: 'adhesion', label: t('contact.subject_membership') },
    { value: 'garanties', label: t('contact.subject_guarantees') },
    { value: 'formation', label: t('contact.subject_training') },
    { value: 'partenariat', label: t('contact.subject_partnership') },
    { value: 'reclamation', label: t('contact.subject_complaint') },
    { value: 'autre', label: t('contact.subject_other') }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nom.trim()) newErrors.nom = t('contact.name_required');
    if (!formData.prenom.trim()) newErrors.prenom = t('contact.first_name_required');
    if (!formData.email.trim()) {
      newErrors.email = t('contact.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.invalid_email');
    }
    if (!formData.sujet) newErrors.sujet = t('contact.subject_required');
    if (!formData.message.trim()) {
      newErrors.message = t('contact.message_required');
    } else if (formData.message.length < 20) {
      newErrors.message = t('contact.message_min_length');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t('contact.form_errors'));
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/api/contact`, {
        ...formData,
        destinataire: 'contact@objectifscopoutremer.com'
      });
      
      setSubmitted(true);
      toast.success(t('contact.message_sent'));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || t('contact.message_error'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="text-center">
            <CardContent className="pt-12 pb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('contact.message_sent_title')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                {t('contact.message_sent_description')} <strong>{formData.email}</strong>.
              </p>
              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={() => navigate('/')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('contact.back_to_home')}
                </Button>
                <Button onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    entreprise: '',
                    sujet: '',
                    message: ''
                  });
                }}>
                  {t('contact.send_another')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="mb-6 flex justify-center">
            <BackButton label={t('contact.back')} />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('contact.page_title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('contact.page_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Informations de contact */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  {t('contact.our_coordinates')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">{t('contact.address')}</p>
                    <p className="text-sm text-gray-600">
                      13 rue Rodrigue YOUYOUTE<br />
                      97139 Les Abymes<br />
                      Guadeloupe
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 flex-shrink-0 text-gray-400 mt-1" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">Email</p>
                    <a 
                      href="mailto:contact@objectifscopoutremer.com" 
                      className="break-words text-sm text-blue-600 hover:underline"
                    >
                      contact@objectifscopoutremer.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 flex-shrink-0 text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">{t('contact.phone')}</p>
                    <a href="tel:+590590460521" className="text-sm text-blue-600 hover:underline">
                      +590 590 46 05 21
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">{t('contact.opening_hours')}</p>
                    <p className="text-sm text-gray-600">
                      {t('contact.weekdays_hours')}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">Heure de Guadeloupe — UTC−4</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Services rapides */}
            <Card className="bg-gradient-to-br from-[#1e3a5f] to-[#2d5a87] text-white">
              <CardHeader>
                <CardTitle className="text-white">{t('contact.need_quick_help')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-white/10 border-white/20 text-white hover:bg-white/20"
                  onClick={() => navigate('/register')}
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('contact.create_account', 'Créer mon espace')}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-white/10 border-white/20 text-white hover:bg-white/20"
                  onClick={() => navigate('/garanties')}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t('contact.solidarity_guarantees')}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Formulaire de contact */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('contact.send_us_message')}</CardTitle>
                <CardDescription>
                  {t('contact.form_description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Nom et Prénom */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nom">{t('contact.last_name')} *</Label>
                      <Input
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        placeholder={t('contact.your_name')}
                        className={errors.nom ? 'border-red-500' : ''}
                      />
                      {errors.nom && (
                        <p className="text-sm text-red-500 mt-1">{errors.nom}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="prenom">{t('contact.first_name')} *</Label>
                      <Input
                        id="prenom"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        placeholder={t('contact.your_first_name')}
                        className={errors.prenom ? 'border-red-500' : ''}
                      />
                      {errors.prenom && (
                        <p className="text-sm text-red-500 mt-1">{errors.prenom}</p>
                      )}
                    </div>
                  </div>

                  {/* Email et Téléphone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">{t('contact.email')} *</Label>
                      <EmailInput
                        value={formData.email}
                        onChange={(value) => setFormData(prev => ({ ...prev, email: value }))}
                        error={errors.email}
                      />
                    </div>
                    <div>
                      <Label htmlFor="telephone">{t('contact.phone')}</Label>
                      <PhoneInput
                        id="telephone"
                        value={formData.telephone}
                        onChange={(value) => setFormData(prev => ({ ...prev, telephone: value }))}
                        defaultCountryCode="GP"
                        showLabel={false}
                      />
                    </div>
                  </div>

                  {/* Entreprise */}
                  <div>
                    <Label htmlFor="entreprise">{t('contact.company_organization')}</Label>
                    <Input
                      id="entreprise"
                      name="entreprise"
                      value={formData.entreprise}
                      onChange={handleChange}
                      placeholder={t('contact.company_placeholder')}
                    />
                  </div>

                  {/* Sujet */}
                  <div>
                    <Label htmlFor="sujet">{t('contact.subject')} *</Label>
                    <select
                      id="sujet"
                      name="sujet"
                      value={formData.sujet}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.sujet ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">{t('contact.select_subject')}</option>
                      {subjects.map((subject) => (
                        <option key={subject.value} value={subject.value}>
                          {subject.label}
                        </option>
                      ))}
                    </select>
                    {errors.sujet && (
                      <p className="text-sm text-red-500 mt-1">{errors.sujet}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message">{t('contact.message')} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.describe_request')}
                      rows={6}
                      className={errors.message ? 'border-red-500' : ''}
                    />
                    <div className="flex justify-between mt-1">
                      {errors.message ? (
                        <p className="text-sm text-red-500">{errors.message}</p>
                      ) : (
                        <span></span>
                      )}
                      <span className="text-xs text-gray-400">
                        {formData.message.length} {t('contact.characters')}
                      </span>
                    </div>
                  </div>

                  {/* RGPD Notice */}
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                    <p>
                      {t('contact.rgpd_notice')}{' '}
                      <a href="/politique-rgpd" className="text-blue-600 hover:underline">
                        {t('contact.privacy_policy')}
                      </a>.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    className="w-full bg-[#d4a33d] hover:bg-[#c4932d] text-white"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('contact.sending')}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t('contact.send_message')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
